import { useEffect, useMemo, useState } from "react";
import type { GeoEvent, Signal, WireInsight } from "@/types/api";
import { SEVERITY_COLOR } from "@/types/api";
import { normalizeSignal, sourceLabelForEventId } from "@/lib/normalize";
import { config } from "@/config";
import { getIdToken } from "@/lib/auth-context";

/**
 * Situation graph — a connected node-link diagram of the signal's relationships:
 * parent above, the current signal highlighted, raw source events (diamonds) and child
 * signals (circles) below, edges drawn between them. Levels = graph depth; renders any
 * depth the cascade engine produces. Pure SVG, no chart library.
 */

const MAX_DEPTH = 4;
const MAX_CHILDREN = 10;
const MAX_EVENTS = 5;
const MAX_ASSETS = 5;

interface GNode {
  gid: string;            // unique graph id (signals: id; events: evt:<id>; businesses: ast:<id>)
  kind: "signal" | "event" | "asset" | "more";
  refId: string;          // signal/event id for click-through ("" = not clickable)
  label: string;
  color: string;
  level: number;
  parentGid: string | null;
  isCurrent?: boolean;
  /** fuller hover text when the label is a bare ticker */
  tip?: string;
}

async function authedGet<T>(path: string): Promise<T | null> {
  try {
    const token = await getIdToken();
    const r = await fetch(`${config.restUrl}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

function trunc(s: string, n = 48): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

/** Greedy word-wrap for SVG labels (<text> has no native wrapping): at most `maxLines` lines
 *  of ~`maxChars`, ellipsized when the label doesn't fit its slot. Pure. */
export function wrapLabel(label: string, maxChars: number, maxLines = 2): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = "";
  for (const raw of words) {
    const w = raw.length > maxChars ? `${raw.slice(0, Math.max(1, maxChars - 1))}…` : raw;
    const cand = cur ? `${cur} ${w}` : w;
    if (cand.length <= maxChars) { cur = cand; continue; }
    if (lines.length === maxLines - 1) {
      // no room for another line — ellipsize what we have and stop
      cur = cur.length >= maxChars - 1 ? `${cur.slice(0, Math.max(1, maxChars - 1))}…` : `${cur}…`;
      break;
    }
    lines.push(cur);
    cur = w;
  }
  if (cur) lines.push(cur);
  return lines.length ? lines.slice(0, maxLines) : [trunc(label, maxChars)];
}

/** Build the flat node list: optional parent, current, source events + children recursively. */
async function buildGraph(current: Signal, events: GeoEvent[]): Promise<GNode[]> {
  const nodes: GNode[] = [];
  let rootLevel = 0;

  if (current.parentId) {
    const p = await authedGet<WireInsight>(`/api/signals/${encodeURIComponent(current.parentId)}`);
    if (p) {
      const ps = normalizeSignal(p);
      nodes.push({ gid: ps.id, kind: "signal", refId: ps.id, label: trunc(ps.title), color: SEVERITY_COLOR[ps.severity], level: 0, parentGid: null });
      rootLevel = 1;
    }
  }

  nodes.push({
    gid: current.id, kind: "signal", refId: current.id, label: trunc(current.title),
    color: SEVERITY_COLOR[current.severity], level: rootLevel,
    parentGid: current.parentId && nodes.length ? current.parentId : null, isCurrent: true,
  });

  // BFS over children, attaching each signal's source events as leaf diamonds.
  let frontier: Array<{ sig: Signal; level: number }> = [{ sig: current, level: rootLevel }];
  while (frontier.length) {
    const next: typeof frontier = [];
    for (const { sig, level } of frontier) {
      const trigIds = (sig.raw.triggerEventIds ?? []).slice(0, MAX_EVENTS);
      for (const tid of trigIds) {
        const ev = events.find((e) => e.id === tid);
        nodes.push({
          gid: `evt:${sig.id}:${tid}`, kind: "event", refId: tid,
          // Live event → its real title. Otherwise the SOURCE is what matters, not a hex tail:
          // "FIRMS satellite (a1b2c3d4)".
          label: trunc(ev ? (ev.title ?? ev.type) : sourceLabelForEventId(tid), 26),
          color: "var(--amber)", level: level + 1, parentGid: sig.id,
        });
      }
      const hidden = (sig.raw.triggerEventIds ?? []).length - trigIds.length;
      if (hidden > 0) {
        nodes.push({ gid: `more:${sig.id}`, kind: "more", refId: "", label: `+${hidden} events`, color: "var(--amber)", level: level + 1, parentGid: sig.id });
      }

      // Affected BUSINESSES as leaf nodes — the tradeables the situation touches (a quake's
      // shaken companies, a fire's plant). Deduped to company level (one node per ticker/name),
      // listed tickers first. Power outages carry no affectedAssets → no noise there.
      const seenBiz = new Set<string>();
      const bizAll = (sig.assets ?? []).filter((a) => {
        const k = a.ticker ?? a.name;
        if (!k || seenBiz.has(k)) return false;
        seenBiz.add(k);
        return true;
      }).sort((a, b) => Number(Boolean(b.ticker)) - Number(Boolean(a.ticker)));
      const biz = bizAll.slice(0, MAX_ASSETS);
      for (const a of biz) {
        nodes.push({
          gid: `ast:${sig.id}:${a.ticker ?? a.name}`, kind: "asset", refId: "",
          label: a.ticker ?? trunc(a.name, 16),
          tip: a.ticker ? `${a.ticker} — ${a.name}` : a.name,
          color: "var(--green)", level: level + 1, parentGid: sig.id,
        });
      }
      if (bizAll.length > biz.length) {
        nodes.push({ gid: `astmore:${sig.id}`, kind: "more", refId: "", label: `+${bizAll.length - biz.length} cos`, color: "var(--green)", level: level + 1, parentGid: sig.id });
      }
      if (level - rootLevel < MAX_DEPTH) {
        const j = await authedGet<{ children?: WireInsight[] }>(`/api/signals/${encodeURIComponent(sig.id)}/children`);
        for (const raw of (j?.children ?? []).slice(0, MAX_CHILDREN)) {
          const c = normalizeSignal(raw);
          if (nodes.some((n) => n.gid === c.id)) continue; // cycle guard
          nodes.push({ gid: c.id, kind: "signal", refId: c.id, label: trunc(c.title), color: SEVERITY_COLOR[c.severity], level: level + 1, parentGid: sig.id });
          next.push({ sig: c, level: level + 1 });
        }
      }
    }
    frontier = next;
  }
  return nodes;
}

const LEVEL_H = 62;
const PAD_X = 14;
const PAD_Y = 20;
const LABEL_H = 21; // room for two wrapped label lines under the bottom level
const VIEW_W = 400;
const CHAR_W = 4.5; // ≈ px per character at the .sg-label font size — drives per-slot wrapping

export function SituationTree({ s, events, onSelectSignal, onSelectEvent }: {
  s: Signal;
  events: GeoEvent[];
  onSelectSignal: (id: string) => void;
  onSelectEvent: (id: string) => void;
}) {
  const [nodes, setNodes] = useState<GNode[] | null>(null);

  useEffect(() => {
    let alive = true;
    setNodes(null);
    buildGraph(s, events).then((n) => { if (alive) setNodes(n); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id, s.updatedAt]);

  const layout = useMemo(() => {
    if (!nodes) return null;
    const levels = new Map<number, GNode[]>();
    for (const n of nodes) {
      const arr = levels.get(n.level) ?? [];
      arr.push(n);
      levels.set(n.level, arr);
    }
    const pos = new Map<string, { x: number; y: number }>();
    const levelCounts = new Map<number, number>();
    const maxLevel = Math.max(...nodes.map((n) => n.level));
    for (const [level, arr] of levels) {
      levelCounts.set(level, arr.length);
      const span = VIEW_W - PAD_X * 2;
      arr.forEach((n, i) => {
        const x = PAD_X + (span * (i + 1)) / (arr.length + 1);
        const y = PAD_Y + level * LEVEL_H;
        pos.set(n.gid, { x, y });
      });
    }
    const height = PAD_Y * 2 + maxLevel * LEVEL_H + LABEL_H + 8;
    return { pos, height, levelCounts };
  }, [nodes]);

  if (!nodes || !layout) {
    return (
      <div className="dsec">
        <div className="h">Situation Graph</div>
        <div className="empty-hint">Mapping relationships…</div>
      </div>
    );
  }

  return (
    <div className="dsec">
      <div className="h">Situation Graph</div>
      <svg className="sgraph" viewBox={`0 0 ${VIEW_W} ${layout.height}`} width="100%" role="img" aria-label="Signal relationship graph">
        {/* edges under nodes */}
        {nodes.filter((n) => n.parentGid && layout.pos.has(n.parentGid)).map((n) => {
          const a = layout.pos.get(n.parentGid!)!;
          const b = layout.pos.get(n.gid)!;
          const midY = (a.y + b.y) / 2;
          return (
            <path key={`e-${n.gid}`} className="sg-edge"
              d={`M ${a.x} ${a.y + 8} C ${a.x} ${midY}, ${b.x} ${midY}, ${b.x} ${b.y - 8}`} />
          );
        })}
        {nodes.map((n) => {
          const p = layout.pos.get(n.gid)!;
          const clickable = n.kind === "signal" ? !n.isCurrent : n.kind === "event" && events.some((e) => e.id === n.refId);
          const onClick = () => {
            if (n.kind === "signal" && !n.isCurrent) onSelectSignal(n.refId);
            else if (n.kind === "event" && clickable) onSelectEvent(n.refId);
          };
          // Width-bound the label to its slot: with 3+ nodes on a level the old single-line
          // labels overlapped — wrap onto (at most) two lines sized to the space between
          // neighbouring node centers instead.
          const count = layout.levelCounts.get(n.level) ?? 1;
          const slotPx = (VIEW_W - PAD_X * 2) / (count + 1);
          const maxChars = Math.max(7, Math.min(26, Math.floor(slotPx / CHAR_W)));
          const lines = wrapLabel(n.label, maxChars);
          return (
            <g key={n.gid} className={`sg-node${clickable ? " clickable" : ""}${n.isCurrent ? " current" : ""}`} onClick={onClick}>
              <title>{n.tip ?? n.label}</title>
              {n.kind === "signal" ? (
                <circle cx={p.x} cy={p.y} r={n.isCurrent ? 9 : 7}
                  fill={n.isCurrent ? n.color : "rgba(0,0,0,0.65)"} stroke={n.color} strokeWidth={n.isCurrent ? 2 : 1.5} />
              ) : n.kind === "asset" ? (
                <rect x={p.x - 4.5} y={p.y - 4.5} width={9} height={9} rx={1}
                  fill="rgba(0,0,0,0.65)" stroke={n.color} strokeWidth={1.3} />
              ) : (
                <rect x={p.x - 5} y={p.y - 5} width={10} height={10} transform={`rotate(45 ${p.x} ${p.y})`}
                  fill="rgba(0,0,0,0.65)" stroke={n.color} strokeWidth={1.2} opacity={n.kind === "more" ? 0.5 : 1} />
              )}
              <text x={p.x} y={p.y + (n.isCurrent ? 22 : 20)} textAnchor="middle" className={`sg-label ${n.kind}`}>
                {lines.map((ln, i) => (
                  <tspan key={i} x={p.x} dy={i === 0 ? 0 : 9}>{ln}</tspan>
                ))}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="empty-hint" style={{ marginTop: 4 }}>
        ● signals (colored by severity, filled = this one) · ◆ source events · ■ businesses/tickers · click to jump
      </div>
    </div>
  );
}
